import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useThemeMode } from '../../ThemeContext';
import { Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend
} from 'chart.js';

// Register Chart.js components
ChartJS.register(ArcElement, Tooltip, Legend);

const TasksUpdateChart = ({ dashboardData }) => {
  const { mode } = useThemeMode();
  const navigate = useNavigate();

  // Extract task percentage data from API response
  const taskPercentages = dashboardData?.task_percentage_breakdown || {};
  const completedPercentage = taskPercentages.completed_percentage || 0;
  const pendingPercentage = taskPercentages.pending_percentage || 0;
  const inProgressPercentage = taskPercentages.in_progress_percentage || 0;
  
  // Calculate actual values based on percentages (for display purposes)
  const totalTasks = dashboardData?.task_overview?.total_tasks || 0;
  const completedTasks = Math.round((completedPercentage / 100) * totalTasks);
  const pendingTasks = Math.round((pendingPercentage / 100) * totalTasks);
  const inProgressTasks = Math.round((inProgressPercentage / 100) * totalTasks);

  // Chart data configuration
  const data = {
    labels: ['Completed Tasks', 'Pending Tasks', 'In Progress Tasks'],
    datasets: [
      {
        data: [completedTasks, pendingTasks, inProgressTasks],
        backgroundColor: [
          '#3fcc3d', // Green for completed
          '#fc4949', // Red for pending  
          '#26c3c0'  // Teal for in progress
        ],
        borderColor: mode === 'dark' ? '#1a1a1a' : '#ffffff',
        borderWidth: 3,
        cutout: '70%', // Creates the donut hole
        borderRadius: 8,
        borderJoinStyle: 'round'
      }
    ]
  };

  // Chart options configuration
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false // We'll create custom legend
      },
      tooltip: {
        backgroundColor: mode === 'dark' ? '#333333' : '#ffffff',
        titleColor: mode === 'dark' ? '#ffffff' : '#333333',
        bodyColor: mode === 'dark' ? '#ffffff' : '#333333',
        borderColor: mode === 'dark' ? '#555555' : '#e0e0e0',
        borderWidth: 1,
        cornerRadius: 8,
        displayColors: true,
        callbacks: {
          label: function(context) {
            const percentages = [completedPercentage, pendingPercentage, inProgressPercentage];
            const percentage = percentages[context.dataIndex].toFixed(1);
            return `${context.label}: ${context.parsed} (${percentage}%)`;
          }
        }
      }
    },
    elements: {
      arc: {
        borderWidth: 3,
        borderJoinStyle: 'round'
      }
    }
  };

  return (
    <div 
      onClick={() => navigate('/manage-task')}
      style={{
        backgroundColor: mode === 'dark' ? '#1a1a1a' : '#ffffff',
        borderRadius: '12px',
        padding: '24px',
        border: mode === 'dark' ? '1px solid #333' : '1px solid #e0e0e0',
        height: '280px',
        overflow: 'hidden',
        cursor: 'pointer',
        transition: 'transform 0.2s ease, box-shadow 0.2s ease'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = mode === 'dark' 
          ? '0 4px 12px rgba(0, 0, 0, 0.5)' 
          : '0 4px 12px rgba(0, 0, 0, 0.1)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = 'none';
      }}
    >
      {/* Header */}
      <div style={{
        marginBottom: '24px'
      }}>
        <h3 style={{
          fontSize: '18px',
          fontWeight: '600',
          margin: 0,
          color: mode === 'dark' ? '#ffffff' : '#333333',
          fontFamily: 'Urbanist, Arial, sans-serif'
        }}>
          Tasks Update
        </h3>
      </div>

      {/* Chart Container */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '24px',
        height: '200px'
      }}>
        {/* Chart with Center Text */}
        <div style={{ 
          position: 'relative', 
          width: '150px', 
          height: '150px',
          flexShrink: 0
        }}>
          <Doughnut data={data} options={options} />
          
          {/* Center text overlay */}
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            textAlign: 'center',
            pointerEvents: 'none'
          }}>
            <div style={{
              fontSize: '28px',
              fontWeight: '700',
              color: mode === 'dark' ? '#ffffff' : '#333333',
              fontFamily: 'Urbanist, Arial, sans-serif',
              lineHeight: '1'
            }}>
              {totalTasks}
            </div>
            <div style={{
              fontSize: '12px',
              color: mode === 'dark' ? '#888888' : '#666666',
              fontFamily: 'Urbanist, Arial, sans-serif',
              marginTop: '2px'
            }}>
              Total
            </div>
          </div>
        </div>

        {/* Legend with colored dots, counts, and labels (count just before label) */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          flex: 1
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <span style={{
              fontSize: '16px',
              fontWeight: '600',
              color: mode === 'dark' ? '#ffffff' : '#111827',
              fontFamily: 'Urbanist, Arial, sans-serif',
              whiteSpace: 'nowrap'
            }}>
              {completedTasks}
            </span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
              <div style={{
                width: '10px',
                height: '10px',
                borderRadius: '50%',
                backgroundColor: '#3fcc3d'
              }}></div>
              <span style={{
                fontSize: '14px',
                color: mode === 'dark' ? '#E5E7EB' : '#6B7280',
                fontFamily: 'Urbanist, Arial, sans-serif'
              }}>
                Completed Tasks
              </span>
            </div>
          </div>
          
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <span style={{
              fontSize: '16px',
              fontWeight: '600',
              color: mode === 'dark' ? '#ffffff' : '#111827',
              fontFamily: 'Urbanist, Arial, sans-serif',
              whiteSpace: 'nowrap'
            }}>
              {pendingTasks}
            </span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
              <div style={{
                width: '10px',
                height: '10px',
                borderRadius: '50%',
                backgroundColor: '#fc4949'
              }}></div>
              <span style={{
                fontSize: '14px',
                color: mode === 'dark' ? '#E5E7EB' : '#6B7280',
                fontFamily: 'Urbanist, Arial, sans-serif'
              }}>
                Pending Tasks
              </span>
            </div>
          </div>
          
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <span style={{
              fontSize: '16px',
              fontWeight: '600',
              color: mode === 'dark' ? '#ffffff' : '#111827',
              fontFamily: 'Urbanist, Arial, sans-serif',
              whiteSpace: 'nowrap'
            }}>
              {inProgressTasks}
            </span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
              <div style={{
                width: '10px',
                height: '10px',
                borderRadius: '50%',
                backgroundColor: '#26c3c0'
              }}></div>
              <span style={{
                fontSize: '14px',
                color: mode === 'dark' ? '#E5E7EB' : '#6B7280',
                fontFamily: 'Urbanist, Arial, sans-serif'
              }}>
                In Progress Tasks
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TasksUpdateChart;